import { ReflectionType } from "../../../../../types/domain-types";
import { DtoDynamicTestSet, DtoTestSet } from "../../../../../types/dto-types";
import { CubeSideName } from "../../components/Сube/types";
import { ReflectionCubeSideData } from "./types";
import {
  calculatePercentage,
  calculateReflectionTypeCounts,
} from "../../utils";

/**
 * Filters dynamic test sets by cube side and category test set IDs
 */
export function filterDynamicTestSetsByCategoryAndSide(
  dynamicTestSets: DtoDynamicTestSet[],
  cubeSide: CubeSideName,
  categoryTestSetIds: string[]
): DtoDynamicTestSet[] {
  return dynamicTestSets.filter(
    dts =>
      dts.cubeSide === cubeSide && categoryTestSetIds.includes(dts.testSetId)
  );
}

/**
 * Builds reflection type distribution with percentages
 */
export function buildReflectionTypeDistribution(
  counts: { challengeCount: number; leverageCount: number },
  total: number
): ReflectionCubeSideData["reflectionTypeDistribution"] {
  const challengePercentage = calculatePercentage(counts.challengeCount, total);
  const leveragePercentage = calculatePercentage(counts.leverageCount, total);

  return {
    [ReflectionType.Challenge]: challengePercentage,
    [ReflectionType.Leverage]: leveragePercentage,
    none: 100 - (challengePercentage + leveragePercentage),
  };
}

/**
 * Builds cube values for reflection types
 */
export function buildCubeValues(
  counts: { challengeCount: number; leverageCount: number },
  total: number
): Record<ReflectionType, number> {
  return {
    [ReflectionType.Challenge]: calculatePercentage(
      counts.challengeCount,
      total
    ),
    [ReflectionType.Leverage]: calculatePercentage(counts.leverageCount, total),
  };
}

function buildSummaryTabs(
  testSetId: string,
  templateTestSet: DtoTestSet | undefined
) {
  return (
    templateTestSet?.scales.map(s => ({
      title: s.scaleName,
      category: s.category,
      content: `AI generated summary for testSetId: ${testSetId}, scale: ${s.scaleName}`,
    })) ?? []
  );
}

/**
 * Builds reflection cube side data for a specific cube side
 */
export function buildReflectionCubeSideData(
  cubeSide: CubeSideName,
  testSet: DtoTestSet | undefined,
  entriesIds: string[],
  leveragesTemplateTestSet: DtoTestSet | undefined,
  challengesTemplateTestSet: DtoTestSet | undefined,
  dynamicTestSetsForSide: DtoDynamicTestSet[],
  totalDynamicTestSets: number
): ReflectionCubeSideData {
  const counts = calculateReflectionTypeCounts(dynamicTestSetsForSide);
  return {
    title: testSet?.info?.name || `Side ${cubeSide}`,
    cubeValues: buildCubeValues(counts, totalDynamicTestSets),
    reflectionTypeDistribution: buildReflectionTypeDistribution(
      counts,
      totalDynamicTestSets
    ),
    summaryTabs: {
      [ReflectionType.Challenge]: buildSummaryTabs(
        testSet?._id || "",
        challengesTemplateTestSet
      ),
      [ReflectionType.Leverage]: buildSummaryTabs(
        testSet?._id || "",
        leveragesTemplateTestSet
      ),
    },
    aiSummaryRequestData: {
      [ReflectionType.Challenge]: {
        templateTestSetId: challengesTemplateTestSet?._id || "",
        testSetId: testSet?._id || "",
        entriesIds,
      },
      [ReflectionType.Leverage]: {
        templateTestSetId: leveragesTemplateTestSet?._id || "",
        testSetId: testSet?._id || "",
        entriesIds,
      },
    },
  };
}
